
import React, { useState, useEffect } from 'react';
import './OfferTimer.css';

interface OfferTimerProps {
  expiresAt: Date;
  compact?: boolean;
}

const OfferTimer: React.FC<OfferTimerProps> = ({ expiresAt, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const difference = expiry - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        expired: false,
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (timeLeft.expired) {
    return <span className="offer-timer expired">Expired</span>;
  }

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 6;
  const isWarning = timeLeft.days === 0 && timeLeft.hours < 24;

  if (compact) {
    if (timeLeft.days > 0) {
      return (
        <span className={`offer-timer compact ${isWarning ? 'warning' : ''}`}>
          {timeLeft.days}d {timeLeft.hours}h left
        </span>
      );
    }
    if (timeLeft.hours > 0) {
      return (
        <span className={`offer-timer compact ${isUrgent ? 'urgent' : 'warning'}`}>
          {timeLeft.hours}h {timeLeft.minutes}m left
        </span>
      );
    }
    return (
      <span className="offer-timer compact urgent">
        {timeLeft.minutes}m {timeLeft.seconds}s left
      </span>
    );
  }

  return (
    <div className={`offer-timer-full ${isUrgent ? 'urgent' : isWarning ? 'warning' : ''}`}>
      <span className="timer-label">Expires in</span>
      <div className="timer-units">
        {timeLeft.days > 0 && (
          <div className="timer-unit">
            <span className="timer-value">{timeLeft.days}</span>
            <span className="timer-suffix">d</span>
          </div>
        )}
        <div className="timer-unit">
          <span className="timer-value">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="timer-suffix">h</span>
        </div>
        <div className="timer-unit">
          <span className="timer-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="timer-suffix">m</span>
        </div>
        {timeLeft.days === 0 && (
          <div className="timer-unit">
            <span className="timer-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
            <span className="timer-suffix">s</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferTimer;
